/**
 * Resolve the concept_id carried on a generated practice item (quiz question
 * or flashcard) back to its lesson concept.
 *
 * Backends may tag an item with EITHER the per-lesson `concept_id` OR the
 * canonical `mem0_memory_id` (the concept-tagging step crosswalks to canonical
 * once mindspace sync has resolved it, and leaves the per-lesson id otherwise).
 * So we index concepts by both ids and accept either. The `canonicalId` is the
 * id FSRS reviews submit against - null when sync hasn't produced it yet
 * (surface it as "pending sync", don't submit).
 */

import type { ConceptInLesson } from '@/lib/api-client';

export interface ResolvedConcept {
  name: string;
  /** Canonical MindspaceConcept id for FSRS submit; null until sync resolves. */
  canonicalId: string | null;
}

export function buildConceptResolver(
  concepts: ConceptInLesson[]
): (itemConceptId: string | null | undefined) => ResolvedConcept | null {
  const byPerLesson = new Map<string, ConceptInLesson>();
  const byCanonical = new Map<string, ConceptInLesson>();
  for (const c of concepts) {
    byPerLesson.set(c.concept_id, c);
    if (c.mem0_memory_id) byCanonical.set(c.mem0_memory_id, c);
  }
  return (itemConceptId): ResolvedConcept | null => {
    if (!itemConceptId) return null;
    const c = byPerLesson.get(itemConceptId) ?? byCanonical.get(itemConceptId);
    if (!c) return null;
    return { name: c.name, canonicalId: c.mem0_memory_id ?? null };
  };
}

export type ConceptResolver = ReturnType<typeof buildConceptResolver>;
